import React, { useState } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowRight, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../../hooks/useThemeMode';

const navLinks = [
  { label: 'Protocol', href: '#how-it-works' },
  { label: 'Architecture', href: '#features' },
  { label: 'Ecosystem', href: '#chains' },
  { label: 'Agents', href: '#agents' },
];

const resourceLinks = [
  { label: 'Whitepaper', href: '/technical-paper' },
  { label: 'Docs', href: '/docs' },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { scrollY } = useScroll();
  const navigate = useNavigate();
  const { isDark } = useThemeMode();

  const backgroundColor = useTransform(
    scrollY,
    [0, 100],
    isDark ? ["rgba(5, 5, 5, 0)", "rgba(5, 5, 5, 0.85)"] : ["rgba(250, 250, 250, 0)", "rgba(250, 250, 250, 0.85)"]
  );
  const backdropFilter = useTransform(scrollY, [0, 100], ["blur(0px)", "blur(16px)"]);

  return (
    <motion.nav
      style={{ backgroundColor, backdropFilter, WebkitBackdropFilter: backdropFilter }}
      className={`fixed top-0 left-0 right-0 z-40 border-b transition-colors duration-300 ${isDark ? 'border-white/5' : 'border-black/5'}`}
    >
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        {/* Logo */}
        <a href="/" className="flex items-center gap-2 group">
          <img
            src={`${process.env.PUBLIC_URL}/logo/main-logo.png`}
            alt=""
            aria-hidden="true"
            className="h-10 w-10 group-hover:scale-105 transition-transform"
            style={{ objectFit: 'contain' }}
          />
          <img
            src={`${process.env.PUBLIC_URL}/logo/${isDark ? 'minimalist-dark.png' : 'minimalist-light.png'}`}
            alt="InterNull"
            className="h-7 group-hover:scale-105 transition-transform"
            style={{ objectFit: 'contain' }}
          />
        </a>

        {/* Desktop Menu */}
        <div className="hidden md:flex items-center gap-1">
          {/* Nav links with pill hover effect */}
          {navLinks.map(link => (
            <a
              key={link.label}
              href={link.href}
              className={`relative px-4 py-2 text-sm font-mono rounded-full transition-all duration-200 ${
                isDark
                  ? 'text-neutral-400 hover:text-white hover:bg-white/[0.06]'
                  : 'text-neutral-500 hover:text-neutral-900 hover:bg-black/[0.04]'
              }`}
            >
              {link.label}
            </a>
          ))}

          {/* Divider */}
          <div className={`w-px h-5 mx-2 ${isDark ? 'bg-white/10' : 'bg-black/10'}`} />

          {/* Resource links */}
          {resourceLinks.map(link => (
            <a
              key={link.label}
              href={link.href}
              className={`relative px-4 py-2 text-sm font-mono rounded-full transition-all duration-200 ${
                isDark
                  ? 'text-neutral-500 hover:text-white hover:bg-white/[0.06]'
                  : 'text-neutral-400 hover:text-neutral-900 hover:bg-black/[0.04]'
              }`}
            >
              {link.label}
            </a>
          ))}

          {/* Launch App button */}
          <button
            onClick={() => navigate('/easy')}
            className={`group flex items-center gap-2 ml-4 px-5 py-2.5 text-sm font-mono font-medium rounded-full transition-all duration-300 ${
              isDark
                ? 'bg-white text-black hover:bg-neutral-200 hover:shadow-lg hover:shadow-white/10'
                : 'bg-neutral-900 text-white hover:bg-neutral-800 hover:shadow-lg hover:shadow-black/20'
            }`}
          >
            <span>Launch App</span>
            <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>

        {/* Mobile Toggle */}
        <button onClick={() => setIsOpen(!isOpen)} className={`md:hidden p-2 rounded-lg transition-colors ${isDark ? 'text-white hover:bg-white/10' : 'text-neutral-900 hover:bg-black/5'}`}>
          {isOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className={`md:hidden overflow-hidden ${isDark ? 'bg-[#0a0a0a]' : 'bg-white'}`}
          >
            <div className="flex flex-col p-6 gap-1 font-mono text-sm">
              {navLinks.map(link => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={() => setIsOpen(false)}
                  className={`py-3 px-4 rounded-lg transition-colors ${
                    isDark
                      ? 'text-neutral-400 hover:text-white hover:bg-white/5'
                      : 'text-neutral-500 hover:text-neutral-900 hover:bg-black/5'
                  }`}
                >
                  {link.label}
                </a>
              ))}

              <div className={`my-2 border-t ${isDark ? 'border-white/5' : 'border-black/5'}`} />

              {resourceLinks.map(link => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={() => setIsOpen(false)}
                  className={`py-3 px-4 rounded-lg flex items-center justify-between transition-colors ${
                    isDark
                      ? 'text-neutral-400 hover:text-white hover:bg-white/5'
                      : 'text-neutral-500 hover:text-neutral-900 hover:bg-black/5'
                  }`}
                >
                  {link.label}
                  <ExternalLink size={14} className="opacity-40" />
                </a>
              ))}

              <button
                onClick={() => { setIsOpen(false); navigate('/easy'); }}
                className={`mt-4 w-full py-3.5 font-bold rounded-full flex justify-center items-center gap-2 transition-all ${
                  isDark
                    ? 'bg-white text-black hover:bg-neutral-200'
                    : 'bg-neutral-900 text-white hover:bg-neutral-800'
                }`}
              >
                Launch App <ArrowRight size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
